/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { Heading, DividerNew, useWindowSize } from 'sarsaparilla';

import {
    AMENITIES_TYPE,
    KEY_CHARACTERISTICS_TYPE,
    ACCESS_TYPE,
    SPECIAL_SITE_TYPE,
    SEARCH_FILTER_CAMPSITE_ELECTRICAL,
    SEARCH_FILTER_CAMPSITE_ELECTRICAL_REORDERED,
    FCFS_TYPE,
    OCCUPANTS_TYPE,
} from '../../../constants/filters/camping';
import { FiltersTypeProps } from '../../../types/filters';
import { getGroupCount } from '../../../utils/filters';
import { CommonFilter } from './CommonFilter';
import { FirstComeFirstServedFilters } from './FirstComeFirstServedFilters';
import { NumberFieldFilter } from './NumberFieldFilter';

interface MustHaveFiltersProps {
    /** Selected filter groups */
    fg: string[];
    onChange: (
        event: React.ChangeEvent<HTMLInputElement>,
        item: FiltersTypeProps
    ) => void;
    onFilterGroupsChange: (fg: string[]) => void;
    hasDates: boolean;
}

const FILTER_KEYS = [
    ...AMENITIES_TYPE,
    ...SEARCH_FILTER_CAMPSITE_ELECTRICAL,
    ...KEY_CHARACTERISTICS_TYPE,
    ...ACCESS_TYPE,
    ...SPECIAL_SITE_TYPE,
    ...FCFS_TYPE,
    OCCUPANTS_TYPE,
].map((item) => item.key);

/** Amenity, hookup, access and other must-have camping filters */
export function MustHaveFilters({
    fg,
    onChange,
    onFilterGroupsChange,
    hasDates,
}: MustHaveFiltersProps) {
    const mustHavesCount = getGroupCount(FILTER_KEYS, { fg });

    const { width = 0 } = useWindowSize();

    const electricalHookupList =
        width > 1240
            ? SEARCH_FILTER_CAMPSITE_ELECTRICAL_REORDERED
            : SEARCH_FILTER_CAMPSITE_ELECTRICAL;

    return (
        <div>
            <div className="search-filters-section-header rounded-layout">
                <Heading headingLevel={2} appearance="h5">
                    Narrow your requirements. What are your must-haves?
                </Heading>
                <span className="search-filters-section-header__count">
                    {mustHavesCount} {mustHavesCount === 1 ? 'Filter' : 'Filters'}{' '}
                    Selected
                </span>
            </div>
            <div className="search-filters-filter-container">
                <CommonFilter
                    fg={fg}
                    filters={AMENITIES_TYPE}
                    onChange={onChange}
                    title="Amenities"
                />
                <CommonFilter
                    fg={fg}
                    onChange={onChange}
                    filters={electricalHookupList}
                    title="Electrical Hookup"
                />
                <CommonFilter
                    fg={fg}
                    onChange={onChange}
                    filters={KEY_CHARACTERISTICS_TYPE}
                    title="Key Characteristics"
                    hasDivider={false}
                />
                <div className="pt-2">
                    <DividerNew />
                </div>
                <div className="pt-2">
                    <NumberFieldFilter
                        label={OCCUPANTS_TYPE.value}
                        units="guests"
                        max={99}
                        id={OCCUPANTS_TYPE.key}
                        filter={OCCUPANTS_TYPE.key}
                        fg={fg}
                        onFilterGroupsChange={onFilterGroupsChange}
                    />
                </div>
                <CommonFilter
                    fg={fg}
                    onChange={onChange}
                    filters={ACCESS_TYPE}
                    title="Access"
                />
                <CommonFilter
                    fg={fg}
                    onChange={onChange}
                    filters={SPECIAL_SITE_TYPE}
                    title="Special Site Types"
                />
                <FirstComeFirstServedFilters
                    fg={fg}
                    onChange={onChange}
                    hasDates={hasDates}
                />
            </div>
        </div>
    );
}

// cSpell:ignore FCFS
